import { addMonths, endOfMonth, endOfQuarter, format, isValid, parseISO, subQuarters } from 'date-fns';

/**
 * Rules for which reporting dates a user may pick (code-plan/qfu/plan-qfuRederiveEndpoint.md §2.4).
 *
 * A rule supplies the selectable options and validates a value, so the control that uses it never
 * encodes a calendar itself. Add a rule here (half-year ends, month ends, a client's own calendar)
 * and pass it as `dateRule`; nothing else changes.
 *
 * Shape: { key, label, options(now) -> [{ value: 'yyyy-MM-dd', label }], defaultValue(now), validate(value) -> null | message, format(value) -> display }
 * `options` serves a dropdown; the re-derive control uses the system date picker and only needs defaultValue, validate and format.
 */

const ISO = 'yyyy-MM-dd';
const DISPLAY = 'dd MMM yyyy';

const option = (date) => ({ value: format(date, ISO), label: format(date, DISPLAY) });

/** Quarter ends only (31 Mar, 30 Jun, 30 Sep, 31 Dec): what a Quarterly Fund Update reports against. */
export const QUARTER_END = {
    key: 'QUARTER_END',
    label: 'Quarter end',
    options: (now = new Date(), count = 8) => {
        const latest = endOfQuarter(subQuarters(now, 1)); // last completed quarter
        return Array.from({ length: count }, (_, i) => option(endOfQuarter(subQuarters(latest, i))));
    },
    defaultValue: (now = new Date()) => format(endOfQuarter(subQuarters(now, 1)), ISO),
    validate: (value) => {
        const date = value ? parseISO(value) : null;
        if (!date || !isValid(date)) return 'Choose a reporting date';
        return format(endOfQuarter(date), ISO) === value ? null : 'The reporting date must be a quarter end';
    },
    format: (value) => format(parseISO(value), DISPLAY),
};

/** Month ends, for a monthly report type. Not used by the QFU; here so the shape has a second example. */
export const MONTH_END = {
    key: 'MONTH_END',
    label: 'Month end',
    options: (now = new Date(), count = 12) => {
        const latest = endOfMonth(addMonths(now, -1));
        return Array.from({ length: count }, (_, i) => option(endOfMonth(addMonths(latest, -i))));
    },
    defaultValue: (now = new Date()) => format(endOfMonth(addMonths(now, -1)), ISO),
    validate: (value) => {
        const date = value ? parseISO(value) : null;
        if (!date || !isValid(date)) return 'Choose a reporting date';
        return format(endOfMonth(date), ISO) === value ? null : 'The reporting date must be a month end';
    },
    format: (value) => format(parseISO(value), DISPLAY),
};

export const REPORTING_DATE_RULES = { QUARTER_END, MONTH_END };
